import { Building2, FileText, Folder, FolderKanban, Hash, List, Tag, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ChatSource, ChatSourceOption } from '@shared/types/chat-sources';
import { chatSourceKey } from '@shared/types/chat-sources';

/** D1 — the « @ » menu above the composer and the chips of the cited sources. */

const SourceIcon = ({ source }: { source: ChatSource }) => {
  const Icon = source.ref.kind === 'knowledge_folder' ? Folder
    : source.ref.kind === 'knowledge_document' ? FileText
      : source.ref.kind === 'app_folder' ? FolderKanban
        : source.ref.kind === 'account' ? Building2
          : source.ref.kind === 'knowledge_tag' ? Hash
        : source.ref.kind === 'app_list' && source.ref.labelIds.length ? Tag : List;
  return <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />;
};

export const SourcePicker = ({
  options,
  searched,
  active,
  failed,
  onPick,
}: {
  options: ChatSourceOption[];
  searched: boolean;
  active: number;
  failed: string | null;
  onPick: (option: ChatSourceOption) => void;
}) => (
  <div role="listbox" aria-label="Sources" className="mb-2 max-h-64 overflow-y-auto rounded-xl border border-domino-cardBorder bg-domino-white py-1 shadow-lg">
    {failed && <p className="px-3 py-2 text-sm text-domino-inkSoft">{failed}</p>}
    {!failed && searched && options.length === 0 && <p className="px-3 py-2 text-sm text-domino-inkSoft">Aucune source</p>}
    {options.map((option, index) => (
      <button
        key={chatSourceKey(option.ref)}
        type="button"
        role="option"
        aria-selected={index === active}
        // Keep the composer focused: the choice is made on mouse down.
        onMouseDown={(event) => {
          event.preventDefault();
          onPick(option);
        }}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-domino-ink',
          index === active ? 'bg-domino-lilac' : 'hover:bg-domino-muted'
        )}
      >
        <SourceIcon source={option} />
        <span className="min-w-0 flex-1 truncate">{option.label}</span>
        {option.detail && <span className="shrink-0 text-domino-inkSoft">{option.detail}</span>}
      </button>
    ))}
  </div>
);

export const SourceChips = ({ sources, onRemove }: { sources: ChatSource[]; onRemove: (source: ChatSource) => void }) => {
  if (!sources.length) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {sources.map((source) => (
        <span
          key={chatSourceKey(source.ref)}
          className="group inline-flex max-w-full items-center gap-1 rounded-full bg-domino-lilac px-2 py-0.5 text-sm text-domino-aubergine"
        >
          <SourceIcon source={source} />
          <span className="truncate">{source.label}</span>
          <button
            type="button"
            aria-label={`Retirer ${source.label}`}
            onClick={() => onRemove(source)}
            className="opacity-0 transition-opacity duration-150 focus:opacity-100 group-hover:opacity-100"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
};
